import axios, { AxiosError, AxiosResponseHeaders } from "axios";
import { getAccessToken, getCurrentUser, logout, setCurrentUser } from "./auth";
import store from "./store";
import getBackendURL from "./api/api";

function isOk(status: number) {
	return status >= 200 && status < 300;
}

export const request = axios.create({
	validateStatus: isOk,
});

request.interceptors.request.use((config) => {
	config.headers["Authorization"] = `Bearer ${getAccessToken()}`;
	return config;
});

let ongoingTokenRefreshRequest: Promise<void> | null = null;

/**
 * When an HTTP 403 is received:
 *   - If no one has initiated token renewal, attempt to log in again (this is a temporary solution)
 *   - Once the token is renewed, retry the original request.
 *   - If the request fails again (whether during login or on the second attempt), abort -- something is wrong.
 */
async function refreshTokenAndTryAgain(error: AxiosError<{ detail: string; geojson?: object }>,) {
	try {
		if (!ongoingTokenRefreshRequest) {
			// The first request to detect 403 is responsible for renewal
			const user = getCurrentUser();
			const hostname = window.location.hostname;
  			const isDeployment = hostname.includes("idot-trec-dev.prairie.illinois.edu") || hostname.includes("idot-trec.prairie.illinois.edu");
			if(!isDeployment){
				ongoingTokenRefreshRequest = request
				.get<{
					access_token: string;
					refresh_token: string;
				}>(`${process.env.REACT_APP_API_SERVER}/clowder/refresh_token`)
				.then(({ data }) => {
					ongoingTokenRefreshRequest = null;
					const user = getCurrentUser();
					if (!user) throw "User not found";
					user.access_token = data.access_token;
					localStorage.setItem("user", JSON.stringify(user));
				});
			}else{
				ongoingTokenRefreshRequest = axios
				.post<{
					access_token: string;
					refresh_token: string;
				}>(getBackendURL("/clowder/refresh_token"), {
					refresh_token: user?.refresh_token,
				})
				.then(({ data }) => {
					ongoingTokenRefreshRequest = null;
					const user = getCurrentUser();
					if (!user) throw "User not found";
					user.access_token = data.access_token;
					user.refresh_token = data.refresh_token;
					setCurrentUser(user);
				});
			}
		}
		await ongoingTokenRefreshRequest;
		// Second attempt
		return request(error.config!);
	} catch {
		// If the second attempt fails, it's unlikely to succeed. Abort to prevent denial-of-service.
		setTimeout(() => {
			logout();
		}, 5000);
		store.dispatch({
			type: "SET_ERROR",
			error: {
				message: `${error.response?.data?.detail} Logging out…`,
			},
		});
		throw new Error("Not authorized.");
	}
}

request.interceptors.response.use(
	function (response) {
		if (
			response.data &&
			typeof response.data == "object" &&
			"new_notifications" in response.data
		) {
			store.dispatch({
				type: "SET_NEW_NOTIFICATIONS",
				open_notifications: response.data["new_notifications"],
			});
		}
		if (
			response.data &&
			typeof response.data == "object" &&
			response.data["error"]
		) {
			store.dispatch({
				type: "SET_ERROR",
				error: {
					message: response.data["error"],
				},
			});
		}
		return response;
	},
	(error: AxiosError<{ detail: string; geojson?: object }>) => {
		if (
			error.response &&
			(error.response.status === 403 || error.response.status === 401)
		) {
			return refreshTokenAndTryAgain(error);
		}
		store.dispatch({
			type: "SET_ERROR",
			error: {
				message:
					error.code === AxiosError.ERR_NETWORK
						? "TREC is currently unable to connect to external or internal services. Please try again later."
						: (error.response?.data.detail ??
							"TREC encountered an internal error."),
				geojson: error.response?.data.geojson,
			},
		});
		throw error;
	},
);

export async function getFileData(url: string) {
	const { data, headers } = await request(url, { responseType: "blob" });
	const contentDisposition = String(
		(headers as AxiosResponseHeaders).get("Content-Disposition") ?? "",
	);
	const filename = contentDisposition.match(/filename="([^"]+)"/)?.[1] ?? "";
	const blobUrl = URL.createObjectURL(data);
	return {
		filename,
		blobUrl,
	};
}

export async function download(url: string) {
	try {
		const { filename, blobUrl } = await getFileData(url);
		const anchorEl = document.createElement("a");
		anchorEl.download = filename;
		anchorEl.href = blobUrl;
		anchorEl.click();
	} catch (e) {
		console.log(e);
	}
}

export async function preview(url: string) {
	try {
		const { blobUrl } = await getFileData(url);
		const anchorEl = document.createElement("a");
		anchorEl.href = blobUrl;
		anchorEl.target = "_blank";
		anchorEl.click();
	} catch (e) {
		console.log(e);
	}
}